/**
 * Handles tab switching functionality for the product details section with elegant transitions.
 * @param {Event} event - The click event object.
 * @param {string} tabName - The ID of the tab content to show.
 */
function openTab(event, tabName) {
  // Early return if no event or tab name is provided.
  if (!event || !tabName) return;

  // Prevent rapid clicking by checking if a transition is already in progress.
  if (window.tabTransitionInProgress) {
    return;
  }

  // Get all tab content elements.
  const tabContents = document.getElementsByClassName('product-details__tab-content');
  const selectedTab = document.getElementById(tabName);

  // Early return if the selected tab doesn't exist.
  if (!selectedTab) return;

  // Check if the clicked tab is already active.
  if (selectedTab.classList.contains('product-details__tab-content--active')) {
    return;
  }

  // Set transition flag to prevent rapid clicks.
  window.tabTransitionInProgress = true;

  // Get all tab button elements.
  const tabLinks = document.getElementsByClassName('product-details__tab-button');

  // Remove active class from all tab buttons.
  Array.from(tabLinks).forEach((link) => {
    link.classList.remove('product-details__tab-button--active');
  });

  // Add active class to the clicked button.
  if (event.currentTarget) {
    event.currentTarget.classList.add('product-details__tab-button--active');
  }

  // Center the active tab if there's overflow.
  centerActiveTab();

  // Find the currently active tab content.
  const activeTabContent = Array.from(tabContents).find((content) =>
    content.classList.contains('product-details__tab-content--active')
  );

  // If there's an active tab, fade it out first.
  if (activeTabContent && activeTabContent !== selectedTab) {
    activeTabContent.classList.add('product-details__tab-content--fade-out');

    // Match the CSS transition duration.
    setTimeout(() => {
      // Hide all tab contents first to ensure clean state.
      Array.from(tabContents).forEach((content) => {
        content.style.display = 'none';
        content.classList.remove('product-details__tab-content--active', 'product-details__tab-content--fade-out');
      });

      // Prepare the new tab for fade-in (hidden but ready).
      selectedTab.style.display = 'block';
      selectedTab.classList.remove('product-details__tab-content--active', 'product-details__tab-content--fade-out');

      // Force a reflow to ensure the transition works.
      selectedTab.getBoundingClientRect();

      // Add active class to trigger fade-in animation.
      selectedTab.classList.add('product-details__tab-content--active');

      // Match the CSS transition duration.
      setTimeout(() => {
        window.tabTransitionInProgress = false;
      }, 400);
    }, 400);
  } else {
    // If no active tab or same tab, just show the selected tab.
    Array.from(tabContents).forEach((content) => {
      content.style.display = 'none';
      content.classList.remove('product-details__tab-content--active', 'product-details__tab-content--fade-out');
    });

    // Prepare the new tab for fade-in.
    selectedTab.style.display = 'block';
    selectedTab.classList.remove('product-details__tab-content--active', 'product-details__tab-content--fade-out');

    // Force a reflow to ensure the transition works.
    selectedTab.getBoundingClientRect();

    // Add active class to trigger fade-in animation.
    selectedTab.classList.add('product-details__tab-content--active');

    // Match the CSS transition duration.
    setTimeout(() => {
      window.tabTransitionInProgress = false;
    }, 400);
  }
}

/**
 * Centers the active tab in the container when there's overflow.
 */
function centerActiveTab() {
  const tabContainer = document.querySelector('.js-product-details-tab-links');
  const activeTab = document.querySelector('.product-details__tab-button--active');

  if (!tabContainer || !activeTab) return;

  // Check if there's horizontal overflow.
  const hasOverflow = tabContainer.scrollWidth > tabContainer.clientWidth;

  if (!hasOverflow) {
    // No overflow, no need to scroll.
    return;
  }

  // Get the current scroll position and container dimensions.
  const containerRect = tabContainer.getBoundingClientRect();
  const activeTabRect = activeTab.getBoundingClientRect();

  // Calculate the center of the container.
  const containerCenter = containerRect.left + containerRect.width / 2;

  // Calculate the center of the active tab.
  const activeTabCenter = activeTabRect.left + activeTabRect.width / 2;

  // Calculate how much we need to scroll to center the active tab.
  const scrollOffset = activeTabCenter - containerCenter;

  // Calculate the new scroll position.
  const newScrollLeft = tabContainer.scrollLeft + scrollOffset;

  // Ensure scroll position is within bounds.
  const maxScrollLeft = tabContainer.scrollWidth - tabContainer.clientWidth;
  const finalScrollLeft = Math.max(0, Math.min(newScrollLeft, maxScrollLeft));

  // Smooth scroll to center the active tab.
  tabContainer.scrollTo({
    left: finalScrollLeft,
    behavior: 'smooth'
  });
}

/**
 * Handles keyboard navigation for tab buttons.
 * @param {KeyboardEvent} event - The keyboard event.
 */
function handleTabKeyboardNavigation(event) {
  /** @type {HTMLElement[]} */
  const tabButtons = Array.from(document.querySelectorAll('.product-details__tab-button'));
  const currentIndex = tabButtons.indexOf(event.target);

  if (currentIndex === -1) return;

  let newIndex = currentIndex;

  switch (event.key) {
    case 'ArrowLeft':
      event.preventDefault();
      newIndex = currentIndex > 0 ? currentIndex - 1 : tabButtons.length - 1;
      break;
    case 'ArrowRight':
      event.preventDefault();
      newIndex = currentIndex < tabButtons.length - 1 ? currentIndex + 1 : 0;
      break;
    case 'Home':
      event.preventDefault();
      newIndex = 0;
      break;
    case 'End':
      event.preventDefault();
      newIndex = tabButtons.length - 1;
      break;
    default:
      return;
  }

  if (newIndex !== currentIndex) {
    const targetButton =
      tabButtons && Array.isArray(tabButtons) && newIndex >= 0 && newIndex < tabButtons.length
        ? tabButtons[`${newIndex}`]
        : null;
    if (targetButton) {
      targetButton.focus();
    }
  }
}

// Initialise the first tab as active when DOM is ready.
document.addEventListener('DOMContentLoaded', () => {
  /** @type {HTMLElement | null} */
  const firstTabButton = document.querySelector('.product-details__tab-button');
  /** @type {HTMLElement | null} */
  const firstTabContent = document.querySelector('.product-details__tab-content');

  if (firstTabButton && firstTabContent) {
    firstTabButton.classList.add('product-details__tab-button--active');
    firstTabContent.style.display = 'block';
  }

  // Add click event listeners to all tab buttons.
  const tabButtons = document.querySelectorAll('.product-details__tab-button');
  tabButtons.forEach((button) => {
    button.addEventListener('click', (event) => {
      const tabName = event.currentTarget.getAttribute('data-tab');
      if (tabName) {
        openTab(event, tabName);
      }
    });

    // Add keyboard navigation event listeners to all tab buttons.
    button.addEventListener('keydown', handleTabKeyboardNavigation);
  });
});
